export interface FuzzyFilterOptions {
  keys: string[];
  threshold?: number;
  isCaseSensitive?: boolean;
  isExactMatches?: boolean;
}

export type KeyOrOptions = string | FuzzyFilterOptions;

const defaultOptions = (options: KeyOrOptions) => {
  const defaults: FuzzyFilterOptions = {
    keys: null,
    threshold: 0.5,
    isCaseSensitive: false,
    isExactMatches: false,
  };

  if (typeof options === 'string') {
    return { ...defaults, keys: [options] };
  }

  if (typeof options !== 'object') {
    return defaults;
  }

  return {
    ...defaults,
    ...options,
  };
};

const normalize = (input: string, caseSensitive: boolean) => {
  return caseSensitive ? input : input.toLowerCase();
};

const isMatch = (input: string, pattern: string, isExactMatches: boolean) => {
  return isExactMatches ? input === pattern : input.indexOf(pattern) > -1;
};

/**
 * Fuzzy matching for collections, based loosely on {@link https://fusejs.io|Fuse.js}.
 *
 *
 */
export function fuzzyFilter<T extends any>(items: T[], options: KeyOrOptions) {
  const { keys, threshold, isCaseSensitive, isExactMatches } = defaultOptions(
    options
  );

  return (term: string): T[] => {
    if (!term || !keys) return items;

    const normalizedTerms: string[] = normalize(term, isCaseSensitive).split(
      ' '
    );

    return items.filter((item) => {
      const normalizedProps = keys.map((key) =>
        normalize(item[key], isCaseSensitive)
      );

      const matchedWords = normalizedTerms.filter((term) => {
        return normalizedProps.some((prop) => {
          return isMatch(prop, term, isExactMatches);
        });
      });

      return matchedWords.length / normalizedTerms.length >= threshold;
    });
  };
}
